import { useEffect, useRef, useState } from 'react';
import { BigNumber } from '@/components/ui/big-number';
import { Card, CardContent } from '@/components/ui/card';
import { useI18n } from '@/hooks/useI18n';
import { useMoneyCopy } from '@/lib/i18n/money-copy';
import {
    SPINNING_AMOUNT_SHAPE,
    SpinningAmount,
} from '@/lib/payments/components/SpinningAmount';
import { formatPlanAmount } from '@/lib/builder/utils/payment-format';
import {
    DUE_WINDOW_ALL,
    IPaymentsKpis,
} from '@/lib/payments/models/payments-dashboard.model';

/**
 * Quanto girano i rulli come minimo, anche se la risposta arriva prima. Il fetch dura
 * gia' almeno LIST_MIN_LOADING_MS, ma e' tarato sullo skeleton delle liste: per un
 * contatore e' un singhiozzo, si vede partire e fermarsi nello stesso istante.
 */
const MIN_SPIN_MS = 500;

/** `loading` che resta vero almeno `minMs` da quando e' diventato vero. */
const useMinSpin = (loading: boolean, minMs: number) => {
    const [spinning, setSpinning] = useState(loading);
    const startedAt = useRef(loading ? Date.now() : 0);

    useEffect(() => {
        if (loading) {
            startedAt.current = Date.now();
            setSpinning(true);
            return;
        }
        const left = minMs - (Date.now() - startedAt.current);
        if (left <= 0) {
            setSpinning(false);
            return;
        }
        const id = window.setTimeout(() => setSpinning(false), left);
        return () => window.clearTimeout(id);
    }, [loading, minMs]);

    return loading || spinning;
};

interface PaymentsKpiRowProps {
    /** Assenti finche' non arriva la prima risposta: quattro segnaposto, stessa altezza. */
    kpis: IPaymentsKpis | undefined;
    dueWindowDays: number;
    /**
     * C'e' una chiamata in corso (primo accesso, filtri, periodo, aggiorna). Le cifre
     * girano anche se ci sono gia' quelle della risposta precedente: sotto filtri nuovi
     * sarebbero numeri vecchi spacciati per la risposta, come le righe delle liste.
     */
    isLoading?: boolean;
}

/**
 * I quattro numeri in cima alla pagina, in una card sola.
 *
 * Erano quattro riquadri separati, ognuno con la sua icona colorata e l'etichetta in
 * maiuscoletto: quattro cornici in fila per dire una cosa sola -- a che punto sta la
 * cassa -- e le icone pesavano piu' delle cifre. Qui e' una riga di numeri dentro un
 * contenitore unico, come le card della Panoramica: si legge da sinistra a destra, e il
 * colore basta a dire quale numero e' un problema.
 *
 * L'ordine e' quello del racconto, non dell'urgenza: quanto vale tutto, quanto e' gia'
 * entrato, quanto e' in ritardo, quanto scade adesso. Il residuo non c'e': e' la
 * differenza fra i primi due, e un quinto numero derivato allungava la fila senza
 * aggiungere una domanda.
 *
 * Sono calcolati dalla RPC sullo stesso insieme filtrato delle liste sotto: cambiare
 * collaboratore o ricerca li muove insieme alle tabelle. Se fossero calcolati qui sui
 * dati gia' scaricati direbbero un'altra cosa, perche' le liste sono troncate.
 *
 * Dal 01/10/2026 (migration 20261001120000) seguono anche il periodo scelto: tutti e
 * quattro contano le rate con scadenza entro oggi + N giorni, le stesse della lista "In
 * scadenza". Prima lo faceva solo l'ultimo, e cambiando periodo gli altri tre restavano
 * fermi. Ora si sommano: totale = incassato + scaduto + in scadenza.
 */
export const PaymentsKpiRow = ({
    kpis,
    dueWindowDays,
    isLoading = false,
}: PaymentsKpiRowProps) => {
    const { t } = useI18n();
    const money = useMoneyCopy();
    const spinning = useMinSpin(isLoading, MIN_SPIN_MS);

    /* Lo scaduto e l'in scadenza restano grigi quando sono zero: uno zero rosso su una
       pagina in ordine fa cercare un problema che non c'e'. */
    const items = [
        {
            label: t('payments.kpi.total'),
            amount: kpis?.total_amount,
            className: undefined,
        },
        {
            label: t(`${money}.paid`),
            amount: kpis?.paid_amount,
            className: 'text-emerald-600',
        },
        {
            label: t('payments.kpi.overdue'),
            amount: kpis?.overdue_amount,
            className: kpis?.overdue_count ? 'text-red-600' : 'text-gray-400',
        },
        {
            label:
                dueWindowDays === DUE_WINDOW_ALL
                    ? t('payments.kpi.dueSoonAll')
                    : t('payments.kpi.dueSoon', { days: dueWindowDays }),
            amount: kpis?.due_soon_amount,
            className: kpis?.due_soon_count ? 'text-amber-600' : 'text-gray-400',
        },
    ];

    return (
        <Card aria-busy={spinning || !kpis}>
            {/* Quattro su una riga sola da sm in su: sono una sequenza da leggere di
                seguito -- quanto vale, quanto e' entrato, quanto e' in ritardo, quanto
                scade -- e spezzarla in due righe la fa leggere come due gruppi. Solo sotto
                sm, dove quattro cifre non ci starebbero senza troncarsi, va a capo in 2x2. */}
            <CardContent className="p-4 grid gap-4 grid-cols-2 sm:grid-cols-4">
                {/* Le etichette ci sono anche al primo accesso -- non dipendono dai dati --
                    e la cifra in caricamento sta nello stesso BigNumber di quella vera:
                    cosi' la card non cambia altezza ne' quando arrivano i dati ne' quando
                    ripartono. */}
                {items.map((item, i) =>
                    !kpis || spinning ? (
                        <BigNumber
                            key={i}
                            label={item.label}
                            value={SPINNING_AMOUNT_SHAPE}
                            valueClassName="text-gray-300"
                            valueContent={<SpinningAmount seed={i} />}
                        />
                    ) : (
                        <BigNumber
                            key={i}
                            label={item.label}
                            value={formatPlanAmount(item.amount)}
                            valueClassName={item.className}
                        />
                    )
                )}
            </CardContent>
        </Card>
    );
};
